import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import { Button, Tag, Space, Tabs } from '@bedrock/components';
import { ProCard, ProTablePlus } from '@bedrock/eed-react';
import { FilterType } from '@bedrock/eed-react/lib/components/ProFilter/interface';
import { IProTableRef } from '@bedrock/eed-react/lib/components/ProTable/interface';
import CopyableNode from '@mpc/common/src/components/CopyableNode';
import {
  fetchAccidentList,
  fetchAccidentTabCounts,
} from '../services/index';
import { supplierDefs } from '@purchase/supplier';

// 列表项运行期还携带 oaNo（OA 流程跳转）与 flowState（已驳回状态判断）：
// 列表 DTO 未声明这些字段（AccidentDetailDto 已声明 oaNo?/flowState?），故本地补齐。
// accidentId / oaNo 是列表行必有的导航字段，声明为非可选。
type AccidentListItem = supplierDefs.AccidentListItemDto & {
  accidentId: number;
  oaNo: string;
  flowState?: string;
};

// TODO: 待对齐真实接口后同步调整筛选条件字段名

/** Tab 配置：待处理 / 全部（协同端） */
const TAB_KEYS = {
  PENDING: 'PENDING',
  ALL: 'ALL',
} as const;
type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

/** 协同端状态 code → { name, color } */
const STATUS_OPTIONS = [
  { value: '4', name: '待处理', color: 'light-orange' },
  { value: '5', name: '审批中', color: 'light-blue' },
  { value: '6', name: '归档', color: 'light-green' },
  { value: '7', name: '已终止', color: 'light-red' },
  { value: 'RETURNED', name: '已驳回', color: 'light-red' },
];
const STATUS_NAME_MAP: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.value, s.name]),
);
const STATUS_COLOR_MAP: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.value, s.color]),
);

/** 即将截止阈值（天）：整改截至时间距今 ≤ 该天数时展示"即将截止" */
const DEADLINE_SOON_DAYS = 10;

type Props = {
  /** 供应商 ID（external 请求头已自动带 Vendor-Id，此处透传用于契约对齐） */
  vendorId?: string;
  /** 查看详情回调（跳协同端事故详情页），由父级决定跳转路径；传事故 accidentId */
  onViewDetail: (accidentId: number) => void;
  /** 处理回调（跳 OA 反馈流程），由父级决定跳转路径；传事故 oaNo */
  onProcess: (oaNo: string) => void;
};

const AccidentList: React.FC<Props> = ({ vendorId, onViewDetail, onProcess }) => {
  const tableRef = useRef<IProTableRef<any>>();

  // —— 返回后恢复列表状态（跳详情/流程后 goBack 回来，筛选不丢）——
  // 筛选条件由 ProFilter dataStore（sessionStorage）恢复；关键词与 Tab 在此恢复，均按供应商隔离
  const listStateKey = `_mpcCache/externalAccidentList/${vendorId || 'current'}/state`;
  const restoredListState = useMemo<{ activeTab?: TabKey; keyword?: string }>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(listStateKey) || '{}');
    } catch {
      return {};
    }
  }, [listStateKey]);
  const isValidTab = (v: unknown): v is TabKey =>
    Object.values(TAB_KEYS).includes(v as TabKey);
  const [activeTab, setActiveTab] = useState<TabKey>(
    isValidTab(restoredListState.activeTab) ? restoredListState.activeTab : TAB_KEYS.PENDING,
  );
  // queryService 读 ref 而非闭包 state：Tab onChange 里 setActiveTab 后同步 query()，
  // state 尚未重渲染，闭包会拿到旧 Tab（曾致点「待处理」发 scope=ALL）
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const [keyword, setKeyword] = useState(restoredListState.keyword || '');
  useEffect(() => {
    try {
      sessionStorage.setItem(listStateKey, JSON.stringify({ activeTab, keyword }));
    } catch {
      // 存储异常（隐私模式/配额）时静默降级为不恢复
    }
  }, [listStateKey, activeTab, keyword]);

  // Tab 计数角标
  const { data: tabCounts } = useRequest(() => fetchAccidentTabCounts());
  const counts = tabCounts?.data;

  const queryService = async ({ params }: any) => {
    const { current, pageSize, paramsWithConditions = {}, keyword } = params || {};

    const cond: Record<string, any> = {};
    Object.entries(paramsWithConditions).forEach(([key, val]: [string, any]) => {
      const v = val?.value;
      if (v !== undefined && v !== '' && v !== null) {
        cond[key] = v;
      }
    });

    // 日期范围 → ISO-8601 字符串（ProTablePlus DATE_RANGE 产出 dayjs.valueOf() 毫秒数，
    // 后端 ZonedDateTime 反序列化只认 ISO-8601，纯数字当纳秒解析 → 垃圾时间）
    const toRange = (range?: (string | number)[]) => {
      if (!range || range.length !== 2) return { start: undefined, end: undefined };
      return {
        start: range[0] != null ? dayjs(range[0]).format('YYYY-MM-DDTHH:mm:ssZ') : undefined,
        end: range[1] != null ? dayjs(range[1]).format('YYYY-MM-DDTHH:mm:ssZ') : undefined,
      };
    };
    const createTime = toRange(cond.createTimeRange);
    const occurDate = toRange(cond.occurDateRange);
    const discoverDate = toRange(cond.discoverDateRange);
    const notifyTime = toRange(cond.notifyTimeRange);
    const rectifyDeadline = toRange(cond.rectifyDeadlineRange);

    const res = await fetchAccidentList({
      pageNo: current || 1,
      pageSize: pageSize || 20,
      scope: activeTabRef.current === TAB_KEYS.PENDING ? 'PENDING' : 'ALL',
      accidentNo: keyword || cond.accidentNo,
      statuses: cond.statuses,
      createTimeStart: createTime.start,
      createTimeEnd: createTime.end,
      occurDateStart: occurDate.start,
      occurDateEnd: occurDate.end,
      discoverDateStart: discoverDate.start,
      discoverDateEnd: discoverDate.end,
      notifyTimeStart: notifyTime.start,
      notifyTimeEnd: notifyTime.end,
      rectifyDeadlineStart: rectifyDeadline.start,
      rectifyDeadlineEnd: rectifyDeadline.end,
    });

    return {
      data: {
        list: res.data?.records || [],
        total: res.data?.total || 0,
      },
    };
  };

  const columns = useMemo(
    () => [
      {
        title: '事故编号',
        dataIndex: 'accidentNo',
        width: 200,
        fixed: 'left' as const,
        render: (_: any, record: AccidentListItem) => {
          const ops = record.operationTypes || [];
          return (
            <CopyableNode value={record.accidentNo}>
              <Space size={4}>
                <Button
                  type="link"
                  onClick={() => {
                    if (ops.includes('handle')) {
                      onProcess(record.oaNo);
                    } else {
                      onViewDetail(record.accidentId);
                    }
                  }}>
                  {record.accidentNo || '--'}
                </Button>
                {record.urgentFlag && <Tag color="red" size="small" text="急" />}
              </Space>
            </CopyableNode>
          );
        },
      },
      {
        title: '处理决定',
        dataIndex: 'handleTypes',
        width: 320,
        render: (v: string[]) =>
          Array.isArray(v) && v.length ? (
            <span className="whitespace-normal">{v.join('，')}</span>
          ) : (
            '--'
          ),
      },
      {
        title: '状态',
        dataIndex: 'statusDisplay',
        width: 120,
        searchRender: {
          name: '当前状态',
          value: 'statuses',
          showCondition: false,
          type: FilterType.SELECT_MULTIPLE,
          filterComponentProps: {
            options: STATUS_OPTIONS.filter(s => s.value !== 'RETURNED').map((s) => ({ name: s.name, value: s.value })),
          },
        },
        render: (v: string, record: AccidentListItem) => {
          const status = record.status || '';
          const isReturned = record.flowState === 'RETURNED';
          const displayKey = isReturned ? 'RETURNED' : status;

          const name = STATUS_NAME_MAP[displayKey] || v || '--';
          const color = STATUS_COLOR_MAP[displayKey] || 'light-grey';

          return status ? <Tag color={color} text={name} /> : '--';
        },
      },
      {
        title: '登记时间',
        dataIndex: 'createTime',
        width: 200,
        searchRender: {
          name: '登记时间',
          value: 'createTimeRange',
          showCondition: false,
          type: FilterType.DATE_RANGE,
        },
        render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '--'),
      },
      {
        title: '通知时间',
        dataIndex: 'notifyTime',
        width: 200,
        searchRender: {
          name: '通知时间',
          value: 'notifyTimeRange',
          showCondition: false,
          type: FilterType.DATE_RANGE,
        },
        render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '--'),
      },
      {
        title: '整改截至时间',
        dataIndex: 'rectifyDeadline',
        width: 200,
        searchRender: {
          name: '整改截至时间',
          value: 'rectifyDeadlineRange',
          showCondition: false,
          type: FilterType.DATE_RANGE,
        },
        render: (v: string, record: any) => {
          if (!v) return '--';
          const deadline = dayjs(v);
          const diffDays = deadline.diff(dayjs(), 'day');
          const isSoon = deadline.isValid() && diffDays >= 0 && diffDays <= DEADLINE_SOON_DAYS && record?.status === '4';
          return (
            <Space size={8}>
              <span>{deadline.isValid() ? deadline.format('YYYY-MM-DD') : v}</span>
              {isSoon && <Tag color="light-red" size="small" text="即将截止" />}
            </Space>
          );
        },
      },
      {
        title: '操作',
        dataIndex: 'operation',
        width: 120,
        fixed: 'right' as const,
        render: (_: any, record: AccidentListItem) => {
          const ops = record.operationTypes || [];
          return (
            <Space size={12}>
              {/* 协同端操作(§4.3.1)：待处理/已驳回→处理(handle)；审批中/归档→查看详情(detail) */}
              {ops.includes('handle') && (
                <Button type="link" onClick={() => onProcess(record.oaNo)}>
                  处理
                </Button>
              )}
              {ops.includes('detail') && (
                <Button type="link" onClick={() => onViewDetail(record.accidentId)}>
                  查看详情
                </Button>
              )}
            </Space>
          );
        },
      },
    ],
    [onViewDetail, onProcess],
  );

  const tabItems = useMemo(
    () => [
      {
        key: TAB_KEYS.PENDING,
        tab: (
          <span>
            待处理
            <Tag text={counts?.PENDING ?? 0} size="xSmall" color="light-red" style={{ marginLeft: 6 }} />
          </span>
        ),
      },
      {
        key: TAB_KEYS.ALL,
        tab: (
          <span>
            全部
            <Tag text={counts?.ALL ?? 0} size="xSmall" style={{ marginLeft: 6 }} />
          </span>
        ),
      },
    ],
    [counts],
  );

  return (
    <ProCard
      title="事故管理"
      level={1}
      expandable={false}
      // 库给 .b-eed-pro-card-body-content 固定 padding-top: 12px，归零后间距挪到下方 Tabs 的 mt-3
      className="[&_.b-eed-pro-card-body-content]:pt-0">
      {/* Tab 行自渲染（不传 toolbar.tabs）：规避 ProTablePlus 配置 tabs 时给工具栏/表格根节点
          强加左右 16px margin（ListToolBar 内联样式 + .b-eed-table-plus CSS），对齐供应商管理页顶格观感 */}
      <Tabs
        activeKey={activeTab}
        size="medium"
        // --rock-tabs-nav-margin 归零：库给 .rock-tabs-nav 默认 margin-bottom 20px（root 变量未被覆盖时生效），
        // 与下方 mb-4 叠加导致 Tab 栏下间距过大；本组件只保留 mb-4（16px）
        // 上方零间距；下方 10px（用户指定）
        className="mb-[10px] [--rock-tabs-nav-margin:0]"
        onChange={(key: string) => {
          const tab = key as TabKey;
          // 先同步 ref 再 query：setActiveTab 后 state 未重渲染，queryService 闭包会读到旧 Tab（曾致点「待处理」发 scope=ALL）
          activeTabRef.current = tab;
          setActiveTab(tab);
          tableRef.current?.query?.();
        }}>
        {tabItems.map((item) => (
          <Tabs.TabPane key={item.key} tab={item.tab} />
        ))}
      </Tabs>
      <ProTablePlus
        ref={tableRef}
        rowKey="accidentId"
        columns={columns}
        request={{ queryService }}
        tableRefresh
        pagination={{ pageSize: 20, pageSizeOptions: [10, 20, 50, 100] }}
        scroll={{ x: 'max-content' }}
        toolbar={{
          search: {
            placeholder: '事故编号',
            id: 'keyword',
            defaultValue: keyword,
            onSearch: (value: unknown) => {
              setKeyword(typeof value === 'string' ? value : '');
            },
          },
          filterSetting: {
            disableAddButton: true,
            showCondition: false,
            // 筛选条件持久化（sessionStorage）：返回本页时由 ProFilter 自动恢复并触发查询
            dataStore: {
              key: `_mpcCache/externalAccidentList/${vendorId || 'current'}/filterValues`,
              fromThe: 'sessionStorage',
              toThe: 'sessionStorage',
            },
          },
        }}
      />
    </ProCard>
  );
};

export default AccidentList;
